import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Filter, Eye, CheckCircle, XCircle, Ban, Star, Store, Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { adminApi } from '@/services/api';
import { formatNPRCompact } from '@/utils/format';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ReasonModal } from '@/components/ReasonModal';
import { REJECTION_REASONS, SUSPENSION_REASONS } from '@/utils/moderation-reasons';
import {
  Badge, Button, Card, CardContent, Input, Skeleton, cn,
} from '@nepal-ecom/ui';

type VendorStatus = 'pending' | 'under_review' | 'approved' | 'rejected' | 'suspended';
type Tier = 'basic' | 'silver' | 'gold' | 'platinum';

interface Vendor {
  id: string;
  storeName: string;
  ownerName: string;
  email: string;
  phone: string;
  status: VendorStatus;
  tier: Tier;
  productCount: number;
  gmv: number;
  rating: number;
  totalReviews: number;
  joinedAt: string;
}

interface ApiVendor {
  id: string;
  businessName: string;
  email: string | null;
  phone: string | null;
  verificationStatus: VendorStatus;
  tier: Tier;
  totalSales: string | number;
  totalOrders: number;
  rating: string | number;
  totalReviews: number;
  createdAt: string;
  ownerName?: string | null;
  productCount?: number;
}

const STATUS_CONFIG: Record<VendorStatus, {
  label: string;
  variant: 'warning' | 'info' | 'success' | 'destructive' | 'secondary';
}> = {
  pending: { label: 'Pending', variant: 'warning' },
  under_review: { label: 'Under Review', variant: 'info' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  suspended: { label: 'Suspended', variant: 'secondary' },
};

function adaptVendor(v: ApiVendor): Vendor {
  return {
    id: v.id,
    storeName: v.businessName,
    ownerName: v.ownerName?.trim() || '—',
    email: v.email ?? '',
    phone: v.phone ?? '',
    status: v.verificationStatus,
    tier: v.tier,
    productCount: v.productCount ?? 0,
    gmv: typeof v.totalSales === 'string' ? Number(v.totalSales) : v.totalSales ?? 0,
    rating: typeof v.rating === 'string' ? Number(v.rating) : v.rating ?? 0,
    totalReviews: v.totalReviews ?? 0,
    joinedAt: v.createdAt,
  };
}

export function VendorList() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modal, setModal] = useState<{ kind: 'reject' | 'suspend'; id: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-vendors', statusFilter],
    queryFn: () =>
      adminApi.getVendors({
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: search || undefined,
      }),
    select: (res) => {
      const payload = res.data?.data;
      const rows: ApiVendor[] = Array.isArray(payload) ? payload : payload?.data ?? [];
      return rows.map(adaptVendor);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveVendor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendors'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.rejectVendor(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendors'] }),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.suspendVendor(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendors'] }),
  });

  const handleApprove = (id: string) => approveMutation.mutate(id);
  const handleReject = (id: string) => setModal({ kind: 'reject', id });
  const handleSuspend = (id: string) => setModal({ kind: 'suspend', id });

  const filtered = (data ?? []).filter(
    (v) =>
      !search ||
      v.storeName.toLowerCase().includes(search.toLowerCase()) ||
      v.ownerName.toLowerCase().includes(search.toLowerCase()) ||
      v.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Vendors"
        description={`${data?.length ?? 0} vendors registered on the platform`}
        actions={
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4" /> Export
          </Button>
        }
      />

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by store, owner, or email…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            >
              <option value="all">All statuses</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Vendor', 'Status', 'Products', 'GMV', 'Rating', 'Joined', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-4 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Store}
                      title="No vendors match your filters"
                      description="Try adjusting search or status filter."
                    />
                  </td>
                </tr>
              )}
              {!isLoading && filtered.map((vendor) => {
                const status = STATUS_CONFIG[vendor.status] ?? { label: vendor.status, variant: 'secondary' as const };
                const canModerate = vendor.status === 'pending' || vendor.status === 'under_review';
                return (
                  <tr key={vendor.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-semibold text-slate-900">{vendor.storeName}</p>
                      <p className="text-xs text-slate-500">{vendor.ownerName} · {vendor.email}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-700 tabular-nums">{vendor.productCount}</td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-slate-900 tabular-nums">
                      {formatNPRCompact(vendor.gmv)}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 fill-secondary-400 text-secondary-400" />
                        <span className="text-sm text-slate-700 tabular-nums">{vendor.rating.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                      {format(new Date(vendor.joinedAt), 'dd MMM yyyy')}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-0.5">
                        <Link
                          to={`/vendors/${vendor.id}`}
                          className={cn(
                            'p-1.5 rounded-md text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors'
                          )}
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {canModerate && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(vendor.id)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(vendor.id)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {vendor.status === 'approved' && (
                          <button
                            type="button"
                            onClick={() => handleSuspend(vendor.id)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Suspend"
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <ReasonModal
        open={modal?.kind === 'reject'}
        title="Reject vendor application"
        description="The vendor will see this reason and can resubmit their KYC."
        presets={REJECTION_REASONS}
        confirmLabel="Reject vendor"
        pending={rejectMutation.isPending}
        onConfirm={(reason) => { if (modal) rejectMutation.mutate({ id: modal.id, reason }); setModal(null); }}
        onClose={() => setModal(null)}
      />
      <ReasonModal
        open={modal?.kind === 'suspend'}
        title="Suspend vendor"
        description="Internal note. The vendor's store will be hidden until reinstated."
        presets={SUSPENSION_REASONS}
        confirmLabel="Suspend vendor"
        pending={suspendMutation.isPending}
        onConfirm={(reason) => { if (modal) suspendMutation.mutate({ id: modal.id, reason }); setModal(null); }}
        onClose={() => setModal(null)}
      />
    </div>
  );
}
